import React, { FC, ReactElement } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldErrors,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';
import { ScheduledEventForm } from '../models/ScheduledEventDto';
import { useTranslation } from 'react-i18next';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { ScheduledRelayCommandConfig } from '@thingslog/repositories';

export const ScheduledRelayCommand: FC<ScheduledRelayCommandProps> = ({
  form,
}: ScheduledRelayCommandProps) => {
  const {
    register,
    control,
    formState: { errors: formErrors },
  } = form;

  const { t } = useTranslation();

  const errors = formErrors.event as FieldErrors<ScheduledRelayCommandConfig>;

  return (
    <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2">
      <div>
        <TextField
          label={t('relay_index')}
          variant="outlined"
          type="number"
          {...register('event.relayIndex', { valueAsNumber: true })}
          error={!!errors?.relayIndex}
          helperText={errors?.relayIndex?.message ?? ''}
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 1 }}
        />
      </div>
      <div>
        <FormControl fullWidth size="small" error={!!errors?.requiredState}>
          <InputLabel id="required-state-label">
            {t('scheduled_task_event_form_relay_state')}
          </InputLabel>
          <Controller
            name="event.requiredState"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'event.requiredState'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId="required-state-label"
                label={t('scheduled_task_event_form_relay_state')}
              >
                <MenuItem value="ON">{t('on')}</MenuItem>
                <MenuItem value="OFF">{t('off')}</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </div>
      <div>
        <FormControl fullWidth size="small" error={!!errors?.hour}>
          <InputLabel id="hour-label">{t('scheduled_task_event_form_hour')}</InputLabel>
          <Controller
            name="event.hour"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'event.hour'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select {...field} labelId="hour-label" label={t('scheduled_task_event_form_hour')}>
                {Array.from({ length: 24 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
                <MenuItem value={255}>N/A</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </div>
      <div>
        <FormControl fullWidth size="small" error={!!errors?.minutes}>
          <InputLabel id="minute-label">{t('scheduled_task_event_form_minute')}</InputLabel>
          <Controller
            name="event.minutes"
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<ScheduledEventForm, 'event.minutes'>;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId="minute-label"
                label={t('scheduled_task_event_form_minute')}
              >
                {Array.from({ length: 60 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
                <MenuItem value={255}>N/A</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </div>
    </div>
  );
};

interface ScheduledRelayCommandProps {
  form: UseFormReturn<ScheduledEventForm, unknown, undefined>;
}
